import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  CreditCard, 
  ExternalLink, 
  TrendingUp
} from 'lucide-react';
import { 
  createSubscriptionCheckout, 
  createCustomerPortal, 
  cancelSubscription 
} from '../../api/worker';
import toast from 'react-hot-toast';

export const BillingSettingsPage = () => {
  const navigate = useNavigate();
  const { node } = useTenant();
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const handleSubscribe = async (tier: string, stripePriceId: string) => {
    if (!node?.id) return;
    setIsSubscribing(tier);
    try {
      const response = await createSubscriptionCheckout(node.id, {
        tier,
        stripePriceId,
        successUrl: window.location.href + '?status=success',
        cancelUrl: window.location.href + '?status=cancelled'
      });
      if (response?.url) {
        window.location.assign(response.url);
      } else {
        toast.error('Failed to initiate subscription checkout.');
      }
    } catch (error) {
      console.error('Failed to subscribe', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setIsSubscribing(null);
    }
  };

  const handleOpenPortal = async () => {
    if (!node?.id) return;
    setIsPortalLoading(true);
    try {
      const response = await createCustomerPortal(node.id, {
        returnUrl: window.location.href
      });
      if (response?.url) {
        window.location.assign(response.url);
      } else {
        toast.error('Failed to load billing portal.');
      }
    } catch (error) {
      console.error('Failed to open billing portal', error);
      toast.error('Could not load billing portal.');
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!node?.id) return;
    if (!window.confirm('Are you sure you want to cancel your monthly subscription? You will maintain access until the end of your billing cycle.')) return;
    setIsCanceling(true);
    try {
      const response = await cancelSubscription(node.id);
      if (response?.success) {
        toast.success(response.message || 'Subscription successfully scheduled for cancellation.');
        window.location.reload();
      } else {
        toast.error(response?.error || 'Failed to cancel subscription.');
      }
    } catch (error) {
      console.error('Failed to cancel subscription', error);
      toast.error('Failed to cancel subscription.');
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Billing Plan</h1>
          <p className="text-xs md:text-sm text-gray-500">Manage your organization's monthly subscription tier and features.</p>
        </div>
      </div>

      <Card className="space-y-6">
        <div className="flex items-center gap-3 border-b border-black dark:border-white pb-4">
          <div className="p-3 bg-indigo-50 text-indigo-700 rounded-none">
            <TrendingUp size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Platform Subscription Plan</h3>
            <p className="text-sm text-gray-500">Upgrade or downgrade your SaaS tier.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 p-6 bg-slate-50 dark:bg-slate-900 border-2 border-black dark:border-white dark:border-white rounded-none flex flex-col justify-between">
            <div className="space-y-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Current Standing</span>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-indigo-700 dark:text-indigo-400 tracking-tight">
                  {node?.tier || 'LOCAL'}
                </span>
                <span className="text-xs text-slate-500 font-bold uppercase">Tier</span>
              </div>
              
              <div className="space-y-1.5 pt-2">
                <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-300">
                  <span>Status:</span>
                  <span className={cn(
                    "font-bold px-2 py-0.5 rounded text-[10px] uppercase",
                    node?.subscriptionStatus === 'active' ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" :
                    node?.subscriptionStatus === 'canceling' ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" :
                    "bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-400"
                  )}>
                    {node?.subscriptionStatus || 'free'}
                  </span>
                </div>
                {node?.currentPeriodEnd && (
                  <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-300">
                    <span>{node?.subscriptionStatus === 'canceling' ? 'Access Ends:' : 'Renews:'}</span>
                    <span className="font-bold">
                      {new Date(node.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 pt-4">
              {node?.stripeCustomerId && (
                <Button 
                  onClick={handleOpenPortal} 
                  isLoading={isPortalLoading}
                  variant="secondary" 
                  className="w-full text-xs font-bold gap-2 h-10 uppercase tracking-widest border-black dark:border-white"
                >
                  Billing Portal
                  <ExternalLink size={14} />
                </Button>
              )}
              {node?.stripeSubscriptionId && node?.subscriptionStatus === 'active' && (
                <Button 
                  onClick={handleCancelSubscription} 
                  isLoading={isCanceling}
                  variant="ghost" 
                  className="w-full text-xs font-bold h-10 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 uppercase tracking-widest"
                >
                  Cancel Plan
                </Button>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Available Tier Upgrades</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { 
                  name: 'Foundation', 
                  price: '29', 
                  tier: 'FOUNDATION', 
                  priceId: import.meta.env.VITE_STRIPE_PRICE_FOUNDATION || 'price_foundation_monthly', 
                  desc: '250 Roster size, Custom branding & low fees.' 
                },
                { 
                  name: 'Momentum', 
                  price: '79', 
                  tier: 'MOMENTUM', 
                  priceId: import.meta.env.VITE_STRIPE_PRICE_MOMENTUM || 'price_momentum_monthly', 
                  desc: '1k Roster size, WebSocket Chat & 2 Levels depth.' 
                },
                { 
                  name: 'Institutional', 
                  price: '199', 
                  tier: 'INSTITUTIONAL', 
                  priceId: import.meta.env.VITE_STRIPE_PRICE_INSTITUTIONAL || 'price_institutional_monthly', 
                  desc: '5k Roster size, 3 Levels depth & high volume credits.' 
                }
              ].map((tierItem) => {
                const isCurrent = node?.tier === tierItem.tier;
                const isHigher = !isCurrent && (
                  (node?.tier === 'LOCAL') || 
                  (node?.tier === 'FOUNDATION' && tierItem.tier !== 'FOUNDATION') || 
                  (node?.tier === 'MOMENTUM' && tierItem.tier === 'INSTITUTIONAL')
                );
                
                return (
                  <div key={tierItem.tier} className={cn(
                    "p-4 border-2 border-black dark:border-white rounded-none flex flex-col justify-between transition-all",
                    isCurrent ? "border-indigo-600 bg-indigo-50/20 dark:bg-indigo-950/10" : "border-black dark:border-white dark:border-white"
                  )}>
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-sm text-gray-900 dark:text-white">{tierItem.name}</span>
                        {isCurrent && <span className="text-[9px] bg-indigo-600 text-white font-black px-1.5 py-0.5 rounded tracking-widest uppercase">Active</span>}
                      </div>
                      <div className="flex items-baseline gap-0.5 mb-2">
                        <span className="text-lg font-black text-gray-900 dark:text-white">${tierItem.price}</span>
                        <span className="text-[10px] text-gray-400 font-bold uppercase">/mo</span>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-normal mb-4">{tierItem.desc}</p>
                    </div>

                    <Button
                      onClick={() => handleSubscribe(tierItem.tier, tierItem.priceId)}
                      disabled={isCurrent || !isHigher}
                      isLoading={isSubscribing === tierItem.tier}
                      className={cn(
                        "w-full h-8 text-[10px] font-black uppercase tracking-widest",
                        isCurrent ? "bg-slate-100 text-slate-400 dark:bg-slate-800 border-none cursor-default" :
                        isHigher ? "bg-indigo-600 hover:bg-indigo-700 text-white border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-indigo-600/15" :
                        "bg-slate-100 text-slate-400 dark:bg-slate-800 border-none"
                      )}
                    >
                      {isCurrent ? 'Current Plan' : (isHigher ? 'Upgrade' : 'Plan Active')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 bg-indigo-50/50 dark:bg-slate-900 border-2 border-black dark:border-white border-indigo-100/30 dark:border-white/40 rounded-none flex gap-3 text-slate-600 dark:text-slate-400 mt-4">
          <CreditCard size={20} className="shrink-0 text-indigo-600 mt-0.5" />
          <div className="text-xs space-y-1">
            <h5 className="font-bold text-gray-900 dark:text-white">Cancellation & Refund Policy</h5>
            <p className="leading-relaxed">
              Stripe billing runs on a recurring monthly cycle. We enforce a strict <strong>no-refund policy</strong> for any partial billing months. If you decide to cancel your subscription, your node will maintain full access to its upgraded features, larger roster limits, higher storage limits, and lowered transaction fees until the end of the active billing cycle.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};